/**
 * The /prices page, one renderer for the server and the client (2026-09-30).
 *
 * The server sent crawlers a table sorted cheapest-first under its own h1 and
 * lede; the client replaced it with the magazine hero, search, sort buttons
 * and a list ordered popular-first, whose 193 links were bare EN paths on
 * every localized page. Both now print this markup from the same data and
 * catalog strings, and the list arrives already painted in the default order.
 */
import { localeHref } from './locale-href.js';
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
const INTL_TAG = {
    en: 'en-US', ru: 'ru-RU', es: 'es-ES', pt: 'pt-BR', tr: 'tr-TR', zh: 'zh-CN',
};
/**
 * Order for the list. The default ("popular") opens on the ten most-travelled
 * destinations, then everything else by the name the visitor reads: plain
 * A→Z opened on Afghanistan, one of the dearest rates in the catalog.
 */
export function sortPriceRows(rows, mode, topSlugs, locale) {
    const collator = new Intl.Collator(INTL_TAG[locale] ?? 'en-US');
    const byName = (a, b) => collator.compare(a.name, b.name);
    if (mode === 'popular') {
        const bySlug = new Map(rows.map((r) => [r.slug, r]));
        const top = topSlugs.slice(0, 10).map((s) => bySlug.get(s)).filter((r) => !!r);
        const inTop = new Set(top.map((r) => r.slug));
        return [...top, ...rows.filter((r) => !inTop.has(r.slug)).sort(byName)];
    }
    const arr = [...rows];
    if (mode === 'az')
        arr.sort(byName);
    if (mode === 'cheap')
        arr.sort((a, b) => a.rateUsdtPerMb - b.rateUsdtPerMb);
    if (mode === 'expensive')
        arr.sort((a, b) => b.rateUsdtPerMb - a.rateUsdtPerMb);
    return arr;
}
/**
 * One row: the per-MB rate is the real tariff, the per-GB figure a muted
 * reference so the four decimals do not lose the reader (operator 2026-05-09).
 */
export function priceRowHtml(c, locale) {
    return `<a class="price-row" href="${esc(localeHref(locale, `/countries/${c.slug}`))}" data-name="${esc(c.nameEn.toLowerCase())}" data-rate="${c.rateUsdtPerMb}">
    <span class="price-row__flag" aria-hidden="true">${esc(c.flag ?? '')}</span>
    <span class="price-row__name">${esc(c.name)}</span>
    <span class="price-row__dots"></span>
    <span class="price-row__price">
      <span class="price-row__price-primary">$${c.rateUsdtPerMb.toFixed(4)}/MB</span>
      <span class="price-row__price-ref">≈ $${c.rateUsdtPerGb.toFixed(2)}/GB</span>
    </span>
    <span class="price-row__arrow">→</span>
  </a>`;
}
export function pricesPageHtml(i) {
    const { t } = i;
    const sortBtn = (mode, key) => `<button class="sort-btn${mode === 'popular' ? ' sort-btn--active' : ''}" data-sort="${mode}">${esc(t(key))}</button>`;
    return `
<section class="hero listing-hero">
  <div class="container">
    <div class="hero__masthead">
      <div class="hero__volume">${esc(t('prices.hero.volume'))}</div>
      <div class="hero__edition">${esc(t('prices.hero.edition'))}</div>
      <div class="hero__date">MMXXVI</div>
    </div>
    <div class="listing-hero__body">
      <div class="section__kicker">${esc(t('prices.hero.kicker'))}</div>
      <h1 class="listing-hero__title">${esc(t('prices.hero.title'))}</h1>
      <p class="listing-hero__lede">${esc(t('prices.hero.lede'))}</p>
    </div>
  </div>
</section>

<section class="prices-controls-band">
  <div class="container">
    <div class="prices-controls">
      <div class="prices-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input type="text" id="priceSearch" placeholder="${esc(t('prices.controls.search'))}" aria-label="${esc(t('prices.controls.searchAria'))}" />
      </div>
      <div class="prices-sort">
        <span class="label">${esc(t('prices.controls.sort'))}</span>
        ${sortBtn('popular', 'prices.controls.popular')}
        ${sortBtn('az', 'prices.controls.az')}
        ${sortBtn('cheap', 'prices.controls.cheap')}
        ${sortBtn('expensive', 'prices.controls.expensive')}
      </div>
    </div>
  </div>
</section>

<section class="section prices-list-section">
  <div class="container">
    <div class="prices-list" id="priceList">${i.rows.map((r) => priceRowHtml(r, i.locale)).join('')}</div>
  </div>
</section>

${i.trialCtaHtml}

<section class="section prices-note">
  <div class="container">
    <div class="section__kicker">${esc(t('prices.note.kicker'))}</div>
    <h2 class="prices-note__title">${esc(t('prices.note.title'))}</h2>
    <p>${esc(t('prices.note.p1'))}</p>
    <p>${esc(t('prices.note.p2'))}</p>
  </div>
</section>
`;
}
